import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FormField } from '../form/FormField';
import { DocumentType, graphql } from '../gql';
import { useModalContext } from '../modal/ModalContext';
import { ModalActions } from '../modal/components/ModalActions';
import { ModalContent } from '../modal/components/ModalContent';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import _ from 'lodash';
import { useSnackbar } from '../store/snackbar';

const journey_DeleteActivity = graphql(`
	mutation journey_activityDelete($id: UUID!) {
		deleteTouchpoint(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const journey_getActivity = graphql(`
	query journey_getActivity($id: UUID!) {
		getTouchpoint(by: { id: $id }) {
			activity {
				description
				notes
				opportunities {
					id
				}
				contentMetrics {
					id
				}
				controlCycle {
					id
				}
				goalsAndObjectives
				links
				owner {
					id
				}
				personas {
					id
				}
				pictures {
					id
				}
				tags {
					id
				}
				actions {
					id
				}
			}
		}
	}
`);

const useDeleteActivity = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { mutate } = useApiMutation(journey_DeleteActivity, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deleteTouchpoint.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'activity.success.deleted' }),
					error: intl.formatMessage({ id: 'activity.error.delete' }),
				},
				errorMessage: result.deleteTouchpoint.errorMessage,
				openSnackbar,
			}),
	});

	return { mutate };
};

type ActivityType = NonNullable<DocumentType<typeof journey_getActivity>['getTouchpoint']>['activity'];

const isObjectEmpty = (obj: ActivityType) => {
	return _.every(obj, (value) => {
		return _.isNull(value) || _.isEmpty(value);
	});
};

export const deleteActivityModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const DeleteActivityModal: FC<z.infer<typeof deleteActivityModalSchema>> = ({ id, name }) => {
	const intl = useIntl();
	const { mutate: deleteActivity } = useDeleteActivity();
	const { data } = useApiQuery(journey_getActivity, { variables: { id } });
	const { closeModal } = useModalContext();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});

	const requiresConfirmation = !isObjectEmpty(data?.getTouchpoint?.activity);

	return (
		<>
			<ModalContent
				isDelete
				requiresConfirmation={requiresConfirmation}
				title={intl.formatMessage({ id: 'activity.delete-modal.title' })}
				description={intl.formatMessage({ id: 'activity.delete-modal.description' }, { name })}
			>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={'50%'} alignSelf={'center'}>
						<form
							noValidate
							onSubmit={(e) => {
								e.preventDefault();
								if (form.formState.isValid) {
									deleteActivity({ id });
									closeModal();
								}
							}}
						>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteActivity({ id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
				disablePrimaryButton={requiresConfirmation ? !form.formState.isValid : false}
			/>
		</>
	);
};
